import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import { CellData } from '@epicuro-next/features/config-strip/utilities/materials';
import { SizeRunsService } from '@epicuro-next/features/config-strip/utilities/size-runs';
import {
  configStripConfigurationDefaultValues,
  ConfigStripConfigurationKeys,
  ConfigStripConfigurations,
  ConfigurationsService,
  CONFIG_STRIP_FEATURE,
  selectConfigurationValueFrom,
  selectProductImageMissingVisual,
} from '@epicuro-next/platform/configuration';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import * as configStripState from '@epicuro-next/state/config-strip-state';
import {
  clearShopQuantities,
  copyQuantities,
  selectCurrentlyActiveDeliveryItems,
  selectDataConfigStripState,
  selectDeliveryItemsMapToShops,
  selectHideInventoryOverviews,
  selectShopDeliveryItemTotals,
  selectSkipInventoryCheckEnabled,
  selectVariant,
} from '@epicuro-next/state/config-strip-state';
import {
  CustomerProductIdentifierUpdate,
  selectLoaded,
  selectProductExtensions,
  updateCustomerProductIdentifier,
} from '@epicuro-next/state/customer-product-identifiers-state';
import * as dabState from '@epicuro-next/state/dab-state';
import {
  getDeliveryShopId,
  getUTCAfternoon,
  isNonNullish,
} from '@epicuro-next/utilities';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  BasketAccountAddress,
  OrderType,
  Product,
  ScaleSets,
} from 'graphql-api/gql-api-interfaces';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  ADAPTIVE_SIZE_GRIP_BREAKPOINT,
  SCALE_SET_TRANSLATIONS,
} from '../../constants';
import { QuantityChangedPayload } from '../../types';

@Component({
  selector: 'epc-materials-grid',
  templateUrl: './materials-grid.component.html',
  styleUrls: ['./materials-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SizeRunsService],
  standalone: false,
})
export class MaterialsGridComponent implements OnDestroy, OnInit {
  /** Component destroy subject */
  private destroy$: Subject<void> = new Subject();

  @Input()
  public basketData$: Observable<void> = new Subject();

  @Input() public compactMdo: boolean;

  public shopTotals$ = this.store.pipe(select(selectShopDeliveryItemTotals));
  public readonly productImageMissingVisualConfig$ = this.store.select(
    selectProductImageMissingVisual,
  );

  private readonly materialTitleAttributes$ = this.configService.getConfigValue(
    {
      namespace: CONFIG_STRIP_FEATURE,
      featureName: ConfigStripConfigurationKeys.MaterialTitleAttributes,
      defaultValue:
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.MaterialTitleAttributes
        ],
    },
  );

  private readonly keepEmptyScales$ = this.configService.getConfigValue({
    namespace: CONFIG_STRIP_FEATURE,
    featureName: ConfigStripConfigurationKeys.KeepEmptyScales,
    defaultValue:
      configStripConfigurationDefaultValues[
        ConfigStripConfigurationKeys.KeepEmptyScales
      ],
  });

  private readonly showNextAvailableDate$ = this.configService.getConfigValue({
    namespace: CONFIG_STRIP_FEATURE,
    featureName: ConfigStripConfigurationKeys.ShowNextAvailableDate,
    defaultValue: true,
  });

  private readonly hasScaleSetTranslation$ = this.configService.getConfigValue({
    namespace: CONFIG_STRIP_FEATURE,
    featureName: ConfigStripConfigurationKeys.ScaleSetTranslation,
    defaultValue: false,
  });

  private readonly showGridInbound$ = this.configService
    .getConfigValue<
      dabState.DabStateConfigurations,
      dabState.DabConfigurationKeys.UseLegacyAggregationApi
    >({
      namespace: dabState.DAB_FEATURE_NAME,
      featureName: dabState.DabConfigurationKeys.UseLegacyAggregationApi,
      defaultValue: true,
    })
    .pipe(map((v) => !v));

  private readonly deliveryWindowType$ = this.store.pipe(
    select(configStripState.selectDeliveryWindowType),
  );

  private readonly useVerticalGrid$ = this.breakpointObserver
    .observe(ADAPTIVE_SIZE_GRIP_BREAKPOINT)
    .pipe(
      map(
        ({ matches }) =>
          matches || this.cdkPlatform.IOS || this.cdkPlatform.ANDROID,
      ),
      distinctUntilChanged(),
    );

  public differentMaterialDimensions$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        ConfigStripConfigurations
      >(
        CONFIG_STRIP_FEATURE,
        ConfigStripConfigurationKeys.DifferentMaterialDimensions,
        configStripConfigurationDefaultValues[
          ConfigStripConfigurationKeys.DifferentMaterialDimensions
        ],
      ),
    ),
    takeUntil(this.destroy$),
  );

  private readonly materials$ = this.store.pipe(
    select(configStripState.selectSortedAvailableVariants),
  );

  private readonly disabledIds$ = this.store.pipe(
    select(configStripState.selectDisabledVariants),
  );

  private readonly isCopyToMasterQuantityPrefilledVisible$ = this.store.select(
    configStripState.selectPrefilledVisible,
  );

  private readonly isNowOrder$ = this.store.pipe(
    select(configStripState.selectCurrentlyActiveOrderType),
    map((orderType) => orderType === OrderType.Now),
  );

  private readonly isSizeGridVisible$ = combineLatest([
    this.store.pipe(
      select(configStripState.selectCurrentlyActiveDeliveryPeriod),
    ),
    this.isNowOrder$,
  ]).pipe(
    map(
      ([activeDeliveryPeriod, isNowOrder]) =>
        activeDeliveryPeriod !== null || isNowOrder,
    ),
  );

  private readonly dabsData$ = this.materials$.pipe(
    mergeMap((materials) => {
      const productKeys = materials.map((product) => ({
        productId: product.productId,
        brandId: product.brandId,
      }));
      return this.store.pipe(select(dabState.selectDabs, productKeys));
    }),
  );

  private readonly productExtensions$ = this.store.pipe(
    select(selectProductExtensions),
  );
  private readonly productExtensionsLoaded$ = this.store.pipe(
    select(selectLoaded),
  );

  private readonly materialsPrices$ = this.store.pipe(
    select(configStripState.selectAvailableVariantsPrices),
  );

  /** scale sets that ara available for the currently selected period */
  private readonly scaleSets$ = combineLatest([
    this.store.pipe(
      select(configStripState.selectScaleSets),
      filter(isNonNullish),
      map((scaleSets: ScaleSets) => Object.keys(scaleSets.scales).sort()),
    ),
  ]).pipe(map(([scales]) => scales));

  private readonly scaleDisplayObject$ = this.hasScaleSetTranslation$.pipe(
    filter((hasScaleTranslations) => !!hasScaleTranslations),
    map(() => SCALE_SET_TRANSLATIONS),
  );

  // this observable is reused to return available scale sets and define if we have invalid sets
  // can not think about better name for observable
  private productScaleSetsForActiveOrderType$ = this.keepEmptyScales$.pipe(
    switchMap((keepScales: boolean) =>
      this.store.pipe(
        select(configStripState.selectAvailableScaleSets(keepScales)),
        withLatestFrom(
          this.store.pipe(select(configStripState.selectProduct)),
          this.store.pipe(
            select(configStripState.selectCurrentlyActiveOrderType),
          ),
        ),
        filter(([, , orderType]) => orderType !== null),
      ),
    ),
  );

  private readonly availableScaleSets$ =
    this.productScaleSetsForActiveOrderType$.pipe(
      filter(([scaleSets]) => !!scaleSets),
      map(([scaleSets]) => scaleSets),
    );

  // if there is a product, but no scale set, it's invalid
  private readonly isScaleSetInvalid$ =
    this.productScaleSetsForActiveOrderType$.pipe(
      filter(([, product]) => !!product),
      tap(([, product]) => this.logInvalidScaleSets(product as Product)),
      map(([scaleSets]) => !scaleSets),
    );

  private readonly activeDeliveryShop$ = this.store.pipe(
    select(configStripState.selectCurrentlyActiveDeliveryShop),
  );

  private readonly selectedScale$ = this.store.pipe(
    select(configStripState.selectSelectedScaleOrDefaultAvailable),
  );

  private readonly hasLockSizeRunSelector$ = this.store.pipe(
    select(configStripState.selectHasLockSizeRunSelector),
  );

  private readonly deliveryItems$ = this.store.pipe(
    select(configStripState.selectCurrentlyActiveDeliveryItems),
  );

  public allDeliveryItems$ = this.store.pipe(
    select(selectDeliveryItemsMapToShops),
  );

  private selectedSize$ = new Subject();

  /**
   * Every time the delivery period changes it is needed to pass the recalculated inventory availability.
   */
  private readonly sizesInventories$ = combineLatest([
    this.store.select(configStripState.selectCurrentlyActiveOrderType),
    this.store.pipe(
      select(configStripState.selectCurrentlyActiveDeliveryPeriod),
    ),
    this.store.pipe(
      select(configStripState.selectCurrentlyActiveDeliveryShopKey),
    ),
    this.store.pipe(select(configStripState.selectPastedData)),
    this.stateActions$.pipe(
      ofType(configStripState.clearShopQuantitiesSuccess),
      startWith(true),
    ),
  ]).pipe(
    withLatestFrom(
      this.store.pipe(
        select(
          configStripState.selectUIAvailableInventoriesForCurrentDeliveryPeriod(
            false,
          ),
        ),
      ),
    ),
    map(([_, inventories]) => inventories),
  );

  private readonly selectCellsQuantities$ = this.store.pipe(
    select(selectCurrentlyActiveDeliveryItems),
  );

  private readonly skipInventoryCheck$ = this.store.pipe(
    select(selectSkipInventoryCheckEnabled),
  );

  private readonly selectedVariantId$ = this.store.pipe(
    select(configStripState.selectCurrentlySelectedVariant),
    map((variant) => variant?.productId),
    first(isNonNullish),
  );

  private readonly isAdvancedMdo$ = combineLatest([
    this.store.pipe(select(configStripState.selectAdvancedMdo)),
    this.useVerticalGrid$,
  ]).pipe(map(([isAmdo, isMobile]) => isAmdo && !isMobile));

  public showAccountId$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PulseConfigurationKeys.DeliveryShowAccountId,
        PulseConfigurations
      >(
        PULSE_FEATURE_NAME,
        PulseConfigurationKeys.DeliveryShowAccountId,
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.DeliveryShowAccountId
        ],
      ),
    ),
  );

  public shops$ = this.store.pipe(
    select(configStripState.selectDeliveryShops),
    withLatestFrom(this.showAccountId$),
    map(([shops, showAccountId]) => {
      const idField = showAccountId ? 'externalAccountId' : 'realAddressId';
      const fallBackField = 'addressId';
      return (shops ?? []).map((deliverable) => ({
        ...deliverable,
        id: getDeliveryShopId({
          accountId: deliverable.accountId,
          addressId: deliverable.addressId,
        }),
        displayId: deliverable[idField] || deliverable[fallBackField],
      }));
    }),
  );

  public sizerunsGrid$ = this.sizeRunsService.hasSizeRuns$;

  public sizeRunsSizes$ = this.sizeRunsService.sizeRuns$;

  public hideInventoryOverviews$ = this.store.pipe(
    select(selectHideInventoryOverviews),
  );

  public data$ = combineLatest([
    this.materials$.pipe(startWith([])),
    this.disabledIds$.pipe(startWith([])),
    this.deliveryItems$.pipe(startWith({})),
    this.materialsPrices$.pipe(startWith({})),
    this.availableScaleSets$.pipe(startWith({})),
    this.isSizeGridVisible$.pipe(startWith(false)),
    this.scaleSets$.pipe(startWith([])),
    this.selectedScale$.pipe(startWith(undefined)),
    this.scaleDisplayObject$.pipe(startWith({})),
    this.materialTitleAttributes$.pipe(startWith({})),
    this.productExtensions$.pipe(startWith({})),
    this.isCopyToMasterQuantityPrefilledVisible$.pipe(startWith(false)),
    this.isScaleSetInvalid$.pipe(startWith(false)),
    this.showGridInbound$.pipe(startWith(false)),
    this.useVerticalGrid$.pipe(startWith(false)),
    this.sizesInventories$.pipe(startWith({})),
    this.isNowOrder$.pipe(startWith(true)),
    this.dabsData$.pipe(startWith({})),
    this.deliveryWindowType$.pipe(startWith(null)),
    this.productExtensionsLoaded$.pipe(startWith(false)),
    this.showNextAvailableDate$.pipe(startWith(false)),
    this.activeDeliveryShop$.pipe(startWith({})),
    this.selectedVariantId$.pipe(startWith('')),
    this.selectCellsQuantities$.pipe(startWith({})),
    this.isAdvancedMdo$,
    this.shops$,
    this.allDeliveryItems$,
    this.shopTotals$,
    this.sizerunsGrid$,
    this.sizeRunsSizes$,
    this.differentMaterialDimensions$,
    this.hideInventoryOverviews$,
    this.skipInventoryCheck$,
    this.hasLockSizeRunSelector$,
  ]).pipe(
    map(
      ([
        materials,
        disabledIds,
        deliveryItems,
        materialsPrices,
        availableScaleSets,
        isSizeGridVisible,
        scaleSets,
        selectedScale,
        scaleDisplayObject,
        materialTitleAttributes,
        productExtensions,
        isCopyToMasterQuantityPrefilledVisible,
        isScaleSetInvalid,
        showGridInbound,
        useVerticalGrid,
        sizesInventories,
        isNowOrder,
        dabsData,
        deliveryWindowType,
        productExtensionsLoaded,
        showNextAvailableDate,
        activeDeliveryShop,
        selectedVariantId,
        selectCellsQuantities,
        isAdvancedMdo,
        deliveryShops,
        allDeliveryItems,
        shopTotals,
        sizerunsGrid,
        sizeRunsSizes,
        differentMaterialDimensions,
        hideInventoryOverviews,
        skipInventoryCheck,
        hasLockSizeRunSelector,
      ]) => ({
        materials,
        disabledIds,
        deliveryItems,
        materialsPrices,
        availableScaleSets,
        isSizeGridVisible,
        scaleSets,
        selectedScale,
        scaleDisplayObject,
        materialTitleAttributes,
        productExtensions,
        isCopyToMasterQuantityPrefilledVisible,
        isScaleSetInvalid,
        showGridInbound,
        useVerticalGrid,
        sizesInventories,
        isNowOrder,
        dabsData,
        deliveryWindowType,
        productExtensionsLoaded,
        showNextAvailableDate,
        activeDeliveryShop,
        selectedVariantId,
        selectCellsQuantities,
        isAdvancedMdo,
        deliveryShops,
        allDeliveryItems,
        shopTotals,
        sizerunsGrid,
        sizeRunsSizes,
        differentMaterialDimensions,
        hideInventoryOverviews,
        skipInventoryCheck,
        hasLockSizeRunSelector,
      }),
    ),
    startWith({}),
  );

  /**
   * Dependencies
   */
  constructor(
    private store: Store,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef,
    private stateActions$: Actions,
    private breakpointObserver: BreakpointObserver,
    private configService: ConfigurationsService,
    private sizeRunsService: SizeRunsService,
    private cdkPlatform: Platform,
  ) {}

  public ngOnInit(): void {
    // Ugly solution alert!
    // availableScaleSets$ is not updating template without this for some reason
    this.availableScaleSets$
      .pipe(distinctUntilChanged(), debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => this.cdr.detectChanges());

    // adding small debounce, so quick navigation between cells won't create an action dispatch hell
    this.selectedSize$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(({ productId, materialId }: CellData) => {
        this.store.dispatch(
          configStripState.selectSize({
            sizeId: productId ?? null,
            variantId: materialId ?? null,
          }),
        );
      });
  }

  /** Angular's OnDestroy hook */
  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets the active size in the state
   * @param productId product id of the material to set as active
   */
  public setActiveSize(cellData: CellData) {
    this.selectedSize$.next(cellData);
  }

  public handleScaleSelection(scale: string) {
    this.store.dispatch(configStripState.selectScale({ scale }));
  }

  public handlePeriodChange(period: string) {
    if (period === OrderType.Now) {
      this.store.dispatch(configStripState.selectNowDeliveryPeriod());
    } else {
      this.store
        .pipe(
          select(configStripState.selectDeliveryShops),
          take(1),
          takeUntil(this.destroy$),
        )
        .subscribe((deliveryShops) => {
          const utcAfternoon = getUTCAfternoon(period);

          this.store.dispatch(
            configStripState.addDeliveryPeriod({
              deliveryPeriod: {
                from: utcAfternoon,
                isTrusted: true,
              },
              deliveryShops: deliveryShops ?? [],
            }),
          );
          this.store.dispatch(
            configStripState.selectFutureDeliveryPeriod({
              deliveryPeriod: { from: utcAfternoon },
            }),
          );
        });
    }
  }

  public quantityChanged({
    changes,
    shop,
  }: {
    changes: QuantityChangedPayload[];
    shop?: BasketAccountAddress;
  }) {
    this.store
      .pipe(
        select(configStripState.selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(
          configStripState.setProductQuantity({
            changes,
            data,
            deliveryShop: shop,
          }),
        ),
      );
  }

  public clearSizeMdoCell(payload: {
    changes: QuantityChangedPayload[];
    shop?: BasketAccountAddress;
  }) {
    this.quantityChanged(payload);
    this.store.dispatch(configStripState.clearShopQuantitiesSuccess());
  }

  public masterQuantitiesPrefill(variantId: string) {
    this.store
      .pipe(
        select(configStripState.selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
        map((data) => {
          const [sizeField, scaleField] = configStripState.getScales(data);
          return {
            variant: Object.entries(data.variants).find(
              ([productId]) => productId === variantId,
            )?.[1],
            sizeField,
            scaleField,
          };
        }),
        filter(({ variant }) => isNonNullish(variant)),
      )
      .subscribe(({ variant, scaleField, sizeField }) =>
        this.store.dispatch(
          configStripState.setMasterPrefilledDeliveryItems({
            // filtered out above
            // tslint:disable:no-non-null-assertion
            variant: variant!,
            sizeField,
            scaleField,
          }),
        ),
      );
  }

  public copyQuantities({
    from,
    to,
  }: {
    from: {
      productId: string;
      shopId: string;
    };
    to: {
      shops: string[];
      products: string[];
    };
  }) {
    this.store
      .pipe(
        select(selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(copyQuantities({ from, to, data })),
      );
  }

  public updateCustomerProductIdentifier(
    update: CustomerProductIdentifierUpdate,
  ) {
    this.store.dispatch(updateCustomerProductIdentifier(update));
  }

  public selectVariant(productId: string) {
    this.store.dispatch(selectVariant({ productId }));
  }

  public clearShop(shop: BasketAccountAddress) {
    const shopId = getDeliveryShopId({
      accountId: shop.accountId,
      addressId: shop.addressId,
    });
    this.store
      .pipe(
        select(selectDataConfigStripState),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((data) =>
        this.store.dispatch(clearShopQuantities({ shopId, data })),
      );
  }

  public selectShop(selectedShops: { accountId: string; addressId: string }) {
    this.store.dispatch(configStripState.setActiveDeliveryShop(selectedShops));
  }

  private logInvalidScaleSets(product: Product) {
    this.logger.message({
      message: `Invalid scale sets for product ${product.brandId}-${product.productId}`,
    });
  }
}
